import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {AngularFireStorage} from '@angular/fire/compat/storage';
import {ActivatedRoute, Router, RouterLink, RouterOutlet} from '@angular/router';
import {environment} from '@env/environment';
import {fuseAnimations} from '@fuse/animations';
import {FuseNavigationService, FuseVerticalNavigationComponent} from '@fuse/components/navigation';
import {FuseMediaWatcherService} from '@fuse/services/media-watcher';
import {selectRole} from '@nahausStore/reducers/app.reducer';
import {Store} from '@ngrx/store';
import {AuthService} from '@shared/services/auth.service';
import {NahausSubscription} from '@subscriptions/models/nahaus-subscription.interface';
import {SubscriptionsService} from '@subscriptions/services/subscriptions.service';
import {NahausTier, NahausTiersOptions, User} from '@users/models/user.interface';
import {NavigationService} from 'app/core/navigation/navigation.service';
import {Navigation} from 'app/core/navigation/navigation.types';
import {UserService} from 'app/core/user/user.service';
import firebase from 'firebase/compat';
import {BehaviorSubject, firstValueFrom, forkJoin, from, of, Subject, take, takeUntil} from 'rxjs';
import {MomentModule} from 'ngx-moment';
import {ToDatePipe} from '../../../../shared/pipes/to-date.pipe';
import {QuickChatComponent} from '../../../common/quick-chat/quick-chat.component';
import {ShortcutsComponent} from '../../../common/shortcuts-old/shortcuts.component';
import {FuseFullscreenComponent} from '@fuse/components/fullscreen/fullscreen.component';
import {LanguagesComponent} from '../../../common/languages-old/languages.component';
import {FuseAlertComponent} from '@fuse/components/alert/alert.component';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {
    AsyncPipe,
    DatePipe,
    I18nPluralPipe,
    NgClass,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault
} from '@angular/common';
import {UserComponent} from '../../../common/user/user.component';
import {
    FuseVerticalNavigationComponent as FuseVerticalNavigationComponent_1
} from '@fuse/components/navigation/vertical/vertical.component';
import {TRANSLOCO_SCOPE, TranslocoModule} from '@ngneat/transloco';
import {FuseLoadingBarComponent} from '@fuse/components/loading-bar/loading-bar.component';
import {NotificationsComponent} from 'app/layout/common/notifications/notifications.component';
import {catchError, filter} from "rxjs/operators";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {getAuthErrorMessage} from "@shared/helpers/auth";
import {computeTrialDaysLeft} from '@subscriptions/models/trial-days-left';
import {Auth} from "@angular/fire/auth";
import {SearchComponent} from "../../../common/search-old/search.component";
import {GlobalSearchComponent} from "../../../common/global-search/global-search.component";
import {MatTooltip} from "@angular/material/tooltip";
import { TutorialService } from '@shared/services/tutorial.service';
import {MatDialog} from '@angular/material/dialog';
import {NahausImage} from '@shared/models/image.interface';
import {ShowImgDialogComponent, ShowImgDialogData} from '@shared/components/show-img-dialog/show-img-dialog.component';
import {ChatCallService} from 'app/modules/admin/chat/calls/chat-call.service';
import {ChatPresenceService} from 'app/modules/admin/chat/chat-presence.service';
import {TeamChatBadgeService} from 'app/modules/admin/chat/team-chat-badge.service';
import {UsersService} from '@users/services/users.service';
import {
    UserAvatarCropperDialogComponent,
    UserAvatarCropperDialogResult
} from '@users/components/user-details/dialogs/user-avatar-cropper-dialog/user-avatar-cropper-dialog.component';


@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: true,
    imports: [FuseLoadingBarComponent, TranslocoModule, MatButtonModule, FuseVerticalNavigationComponent_1, UserComponent, NgIf, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault, MatButtonModule, MatIconModule, FuseAlertComponent, RouterLink, LanguagesComponent, FuseFullscreenComponent, ShortcutsComponent, NotificationsComponent, RouterOutlet, QuickChatComponent, AsyncPipe, DatePipe, I18nPluralPipe, ToDatePipe, MomentModule, MatProgressSpinner, SearchComponent, GlobalSearchComponent, MatTooltip],
    providers: [
        {provide: TRANSLOCO_SCOPE, useValue: 'layout'}
    ],
})

export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    authUser: firebase.User;
    firebaseUser: User;

    isMasterUser: boolean;
    isAdminUser: boolean;
    authUser$: BehaviorSubject<firebase.User>;
    role$;
    isSubscribedUser$: Subject<boolean>;
    tier$: BehaviorSubject<NahausTier>;
    readonly env = environment;

    trialSubscription: NahausSubscription;
    pastDueSubscription: NahausSubscription | null;
    customerObject: any;
    willTrialBeExtended: boolean;

    sendingVerificationLink = false;
    uploadingAvatar = false;
    readonly maxAvatarInputSizeMB = 20;
    readonly allowedAvatarMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];

    tiersOptions = NahausTiersOptions;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private snackbar: MatSnackBar,
        private auth: Auth,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private usersService: UsersService,
        private storage: AngularFireStorage,
        private dialog: MatDialog,
        private store: Store,
        private subscriptionsService: SubscriptionsService,
        private _teamChatBadgeService: TeamChatBadgeService,
        private _chatCallService: ChatCallService,
        private _chatPresenceService: ChatPresenceService,
        public tutorialService: TutorialService
    ) {
        this.authUser$ = this._authService.authUser$;
        this.role$ = this.store.select((state: any) => selectRole(state?.app));
        this.store.select((state: any) => selectRole(state?.app)).subscribe((role) => console.log('on role received', role));
        this.isSubscribedUser$ = this._authService.isSubscribedUser$;
        this.tier$ = this._authService.tier$;
    }

    get currentYear(): number {
        return new Date().getFullYear();
    }

    /** Resttage der Testversion, aufgerundet; dieselbe Zählweise wie Dashboard-Karte, /upgrade und 431-Seite. */
    get trialDaysLeft(): number | null {
        return computeTrialDaysLeft(this.trialSubscription?.trial_end, Date.now());
    }

    get shouldShowTrialActivationCta(): boolean {
        return environment.pool
            && (this.isMasterUser || (this.isAdminUser && this.trialSubscription?.role === NahausTiersOptions.ENTERPRISE))
            && this.trialSubscription?.status === 'trialing';
    }

    get avatarURL(): string | null {
        return this.firebaseUser?.avatar?.url || this.firebaseUser?.avatar?.downloadURL || null;
    }

    ngOnInit(): void {

        this.isMasterUser = this._authService.isMasterUser();
        this.isAdminUser = this._authService.isAdminUser();

        // Unread counter on the "Team Chat" navigation entry (app wide)
        this._teamChatBadgeService.init();

        // Ring for incoming team chat voice calls anywhere in the app
        this._chatCallService.init();

        // Publish my online status + watch the team's (MSN style presence)
        this._chatPresenceService.init();

        this._authService.trialSubscription$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((trialSubscription) => {
                this.trialSubscription = trialSubscription;

                if (!trialSubscription) {
                    this.willTrialBeExtended = false;
                }
            });

        this.getTrialSubscription();
        this.getPastDueSubscription();
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        // this._userService.user$
        //   .pipe((takeUntil(this._unsubscribeAll)))
        //   .subscribe((user: User) => {
        //       this.user = user;
        //   });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        this._authService.firestoreUser$.pipe(takeUntil(this._unsubscribeAll)).subscribe(firebaseUser => {
            console.log('classy component -> firebaseUser ->', firebaseUser);
            this.firebaseUser = firebaseUser;
        });
    }


    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    getTrialSubscription(): void {
        if (!environment.pool || !(this._authService.isMasterUser() || this._authService.isAdminUser())) {
            return;
        }
        forkJoin([
            // get trial sub
            this.subscriptionsService
                .collection(query => query
                    .where('role', "in", [NahausTiersOptions.STARTER, NahausTiersOptions.PRO, NahausTiersOptions.ENTERPRISE])
                    .where('status', '==', 'trialing')
                    .limit(1))
                .get()
                .pipe(take(1), catchError(err => {
                    console.error("Error getting trial subscription", err);
                    return of(null);
                })),
            from(this.subscriptionsService.getCustomerObject()).pipe(take(1), catchError(err => {
                console.error("Error getting customer object", err);
                return of(null);
            }))
        ]).subscribe(([result, customer]: [firebase.firestore.QuerySnapshot<NahausSubscription> | null, any]) => {
            console.log('getTrialSubscription -> result', result);
            if (result && !result.empty) {
                this.trialSubscription = result.docs[0]?.data() as NahausSubscription;
                this.subscriptionsService.authService.trialSubscription$.next(this.trialSubscription);
                if (environment.pool) {
                    this.subscriptionsService.authService.isActiveUser = false;
                }
            } else {
                // no trial --> implicates isActive
                this.trialSubscription = null;
                this.subscriptionsService.authService.trialSubscription$.next(null);
                this.subscriptionsService.authService.isActiveUser = true;
            }
            this.customerObject = customer;
            this.willTrialBeExtended = this.customerObject?.invoice_settings?.default_payment_method && !this.trialSubscription?.cancel_at_period_end;
        });
        // this.subscriptionsService
        //   .collection(query => query.where('status', '==', 'trialing').limit(1))
        //   .get()
        //   .pipe(take(1))
        //   .subscribe((result) => {
        // if (!result.empty) {
        //   this.trialSubscription = result.docs[0]?.data() as NahausSubscription;
        //   this.subscriptionsService.authService.trialSubscription$.next(this.trialSubscription);
        // }
        // });
    }

    getPastDueSubscription(): void {
        if (!environment.pool) {
            return;
        }

        this.subscriptionsService
            .collection(query => query
                .where('role', "in", [NahausTiersOptions.STARTER, NahausTiersOptions.PRO, NahausTiersOptions.ENTERPRISE])
                .where('status', '==', 'past_due')
                .limit(1))
            .valueChanges()
            .pipe(takeUntil(this._unsubscribeAll), catchError(err => {
                console.error("Error getting past due subscription", err);
                return of([]);
            }))
            .subscribe((subscriptions: NahausSubscription[]) => {
                this.pastDueSubscription = subscriptions?.[0] || null;
            });
    }

    sendToCustomerPortal(): Promise<any> {
        return this.subscriptionsService.sendToCustomCustomerPortal({
            return_url: window.location.href,
            locale: 'de'
        });
    }

    goToTrialActivationPricing(): void {
        this._router.navigate(['/upgrade'], {
            queryParams: {
                activateTrial: 'true',
                activationRequestAt: Date.now()
            }
        });
    }

    sendVerificationLink() {
        this.sendingVerificationLink = true;
        this._authService.resendConfirmationEmail()
            .then(async () => {
                this.snackbar.open(
                    'Wir haben Ihnen einen Verifizierungslink per E-Mail geschickt. Nachdem Sie Ihre E-Mail-Adresse bestätigt haben, klicken Sie bitte auf „Aktualisieren“ oder laden Sie die Seite neu.',
                    'Aktualisieren',
                    {duration: 120000}
                ).onAction().subscribe(() => {
                    // this.authUser.reload();
                    this.auth.currentUser.reload();
                });
            })
            .catch((err) => {
                const errorCode = err?.code;
                const message = getAuthErrorMessage(errorCode);
                this.snackbar.open(message, 'OK', {duration: 15000});
                this.sendingVerificationLink = false;
                console.error(err);
            })
            .finally(() => this.sendingVerificationLink = false);
    }

    openAvatarPreview(event?: MouseEvent): void {
        event?.stopPropagation();

        if (!this.avatarURL) {
            return;
        }

        this.dialog.open(ShowImgDialogComponent, {
            autoFocus: false,
            data: {
                url: this.avatarURL,
                title: 'Profilbild'
            } as ShowImgDialogData,
            disableClose: false,
            maxHeight: '90vh',
            panelClass: 'fuse-confirmation-dialog-panel'
        });
    }

    onAvatarFileSelected(fileList: FileList | null, input?: HTMLInputElement): void {
        const file = fileList?.item(0);

        if (input) {
            input.value = '';
        }

        if (!file) {
            return;
        }

        if (!this.allowedAvatarMimeTypes.includes(file.type)) {
            this.snackbar.open('Bitte verwenden Sie ein JPEG, PNG oder WebP Bild.', 'OK', {duration: 5000});
            return;
        }

        if (file.size / 1000 / 1000 > this.maxAvatarInputSizeMB) {
            this.snackbar.open(`Das Bild ist zu groß. Bitte verwenden Sie ein Bild mit max. ${this.maxAvatarInputSizeMB} MB.`, 'OK', {duration: 5000});
            return;
        }

        this.dialog.open(UserAvatarCropperDialogComponent, {
            autoFocus: false,
            data: {
                displayName: this.firebaseUser?.displayName || this.authUser$?.value?.displayName,
                file
            },
            maxHeight: '92vh',
            panelClass: 'user-avatar-cropper-dialog-panel'
        }).afterClosed()
            .pipe(takeUntil(this._unsubscribeAll), filter((result: UserAvatarCropperDialogResult) => !!result?.file))
            .subscribe((result: UserAvatarCropperDialogResult) => this.replaceAvatar(result.file));
    }

    async replaceAvatar(file: File): Promise<void> {
        const userID = this.getCurrentUserID();

        if (!userID) {
            this.snackbar.open('Das Profilbild konnte nicht aktualisiert werden, weil kein Benutzer gefunden wurde.', 'OK', {duration: 6000});
            return;
        }

        const oldAvatar = this.firebaseUser?.avatar || null;
        let uploadedAvatar: NahausImage | null = null;

        try {
            uploadedAvatar = await this.uploadAvatarFile(userID, file);
            await this.usersService.doc(userID).update({avatar: uploadedAvatar});
            this.firebaseUser = {...this.firebaseUser, avatar: uploadedAvatar};
            await this.syncCurrentAuthUserPhotoURL(userID, uploadedAvatar.url);
            await this.deleteAvatarFromStorage(oldAvatar, uploadedAvatar.path);
            this.snackbar.open('Profilbild wurde aktualisiert.', 'OK', {duration: 3500});
        } catch (error) {
            console.error('Error while replacing sidebar avatar:', error);

            if (uploadedAvatar?.path) {
                await this.deleteAvatarFromStorage(uploadedAvatar).catch(err => console.warn('Could not remove failed avatar upload', err));
            }

            this.snackbar.open('Das Profilbild konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.', 'OK', {duration: 6000});
        } finally {
            this.uploadingAvatar = false;
        }
    }

    async uploadAvatarFile(userID: string, file: File): Promise<NahausImage> {
        this.uploadingAvatar = true;

        const imageID = this.usersService.createID();
        const path = this.buildAvatarStoragePath(userID, imageID);
        const ref = this.storage.ref(path);
        const metadata = {
            cacheControl: 'public,max-age=31536000',
            contentType: 'image/jpeg',
            customMetadata: {
                isAvatar: 'true',
                source: 'users'
            }
        };

        await this.storage.upload(path, file, metadata);
        const downloadURL = await firstValueFrom(ref.getDownloadURL());

        return {
            downloadURL,
            id: imageID,
            isAvatar: true,
            name: file.name,
            path,
            size: file.size,
            type: file.type as any,
            url: downloadURL
        };
    }

    buildAvatarStoragePath(userID: string, imageID: string): string {
        if (environment?.pool) {
            if (!this._authService?.customerID) {
                throw new Error('customerID is missing');
            }

            return `customers/${this._authService.customerID}/users/${userID}/avatar/${imageID}.jpg`;
        }

        return `users/${userID}/avatar/${imageID}.jpg`;
    }

    async syncCurrentAuthUserPhotoURL(uid: string, photoURL: string | null): Promise<void> {
        if (this._authService.authUser?.uid !== uid) {
            return;
        }

        const currentUser = await this._authService.auth.currentUser;
        await currentUser?.updateProfile({photoURL});
        await currentUser?.reload();
    }

    async deleteAvatarFromStorage(avatar: NahausImage | null, replacementPath?: string): Promise<void> {
        if (!avatar?.path || avatar.path === replacementPath) {
            return;
        }

        try {
            await firstValueFrom(this.storage.ref(avatar.path).delete());
        } catch (err: any) {
            if (err?.code !== 'storage/object-not-found') {
                throw err;
            }
        }
    }

    private getCurrentUserID(): string | null {
        return this.firebaseUser?.id || this.firebaseUser?.uid || this._authService.authUser?.uid || null;
    }

}
